/**
 * @fileoverview Callout - A versatile callout component for displaying contextual information
 * 
 * This component provides a styled container for displaying important information,
 * warnings, success messages, or general notes with optional icons and titles.
 * 
 * @module Callout
 */

// @ts-nocheck - Suppressing React type conflicts between project and UI library
import React from 'react';
import { Flex, Text, View, IconButton, Icon, Icons } from '@smarterservices/smarter-ui';

/**
 * Available callout variants
 */
export type CalloutVariant = 'info' | 'success' | 'warning' | 'note' | 'danger';

/**
 * Icon configuration for callout
 */
export interface CalloutIcon {
    /** Icon name from the Icons library */
    name: keyof typeof Icons;
    /** Icon size */
    size?: 'x-small' | 'small' | 'medium' | 'large';
}

/**
 * Props for the Callout component
 */
export interface CalloutProps {
    /** Visual variant of the callout */
    variant?: CalloutVariant;
    /** Optional title text */
    title?: string;
    /** Content to display in the callout */
    children: React.ReactNode;
    /** Optional icon configuration */
    icon?: CalloutIcon;
    /** Custom margin override */
    margin?: string;
    /** Custom padding override */
    padding?: 'small' | 'medium' | 'large';
    /** Additional CSS class name */
    className?: string;
}

/**
 * Internal configuration for callout styling
 */
interface CalloutConfig {
    background: string;
    borderColor: 'info' | 'success' | 'warning' | 'danger' | 'secondary';
    iconName: keyof typeof Icons;
    iconColor: 'success' | 'danger' | 'secondary' | 'primary';
    textColor: 'info' | 'success' | 'warning' | 'danger' | 'secondary';
}

/**
 * Type-safe configuration mapping for callout variants
 */
type CalloutTypeConfig = {
    [key in CalloutVariant]: CalloutConfig;
};

/**
 * Callout - A versatile component for displaying contextual information
 * 
 * Displays styled callout boxes with optional icons and titles. Supports multiple
 * variants for different types of messages (info, success, warning, note, danger).
 * 
 * @component
 * @example
 * ```tsx
 * // Basic info callout
 * <Callout variant="info" title="Information">
 *   This is an informational message.
 * </Callout>
 * 
 * // Warning with custom icon
 * <Callout 
 *   variant="warning" 
 *   title="Warning"
 *   icon={{ name: 'warning', size: 'small' }}
 * >
 *   Please review this important information.
 * </Callout>
 * 
 * // Success message
 * <Callout variant="success" title="Success!">
 *   Your changes have been saved.
 * </Callout>
 * ```
 * 
 * @param {CalloutProps} props - Component props
 * @returns {React.ReactElement} Rendered callout component
 */
export function Callout({
    variant = 'info',
    title,
    children,
    icon,
    margin = 'small 0',
    padding = 'small',
    className,
}: CalloutProps): React.ReactElement {
    // Configuration mapping for each variant
    const typeConfig: CalloutTypeConfig = {
        info: {
            background: 'primary',
            borderColor: 'info',
            iconName: 'info',
            iconColor: 'primary',
            textColor: 'info',
        },
        success: {
            background: 'primary',
            borderColor: 'success',
            iconName: 'check',
            iconColor: 'success',
            textColor: 'success',
        },
        warning: {
            background: 'primary',
            borderColor: 'warning',
            iconName: 'warning',
            iconColor: 'danger',
            textColor: 'warning',
        },
        danger: {
            background: 'primary',
            borderColor: 'danger',
            iconName: 'warning',
            iconColor: 'danger',
            textColor: 'danger',
        },
        note: {
            background: 'primary',
            borderColor: 'secondary',
            iconName: 'note',
            iconColor: 'secondary',
            textColor: 'secondary',
        },
    };

    const config = typeConfig[variant];

    // Determine which icon to use (custom or default)
    const iconName = icon?.name || config.iconName;
    const iconSize = icon?.size || 'x-small';

    // Check if the icon exists in the Icons library
    const hasValidIcon = iconName && Icons[iconName];

    return React.createElement(
        View,
        {
            as: 'div',
            background: config.background as any,
            padding,
            margin,
            className,
        },
        React.createElement(
            Flex,
            { direction: 'row', gap: 'xx-small', alignItems: 'start' },
            hasValidIcon &&
            React.createElement(
                Flex.Item,
                null,
                React.createElement(
                    IconButton,
                    {
                        screenReaderLabel: `${variant} callout`,
                        withBackground: false,
                        withBorder: false,
                        color: config.textColor as any,
                        interaction: 'readonly',
                        cursor: 'default',
                        size: 'small',
                        themeOverride: {
                            dangerGhostColor: '#EE6D15',
                            warningColor: '#EE6D15',
                        },
                    },
                    React.createElement(Icon, { name: iconName as any, size: iconSize })
                )
            ),
            React.createElement(
                Flex.Item,
                { shouldGrow: true, shouldShrink: true },
                React.createElement(
                    Flex,
                    { direction: 'column', gap: 'x-small' },
                    title &&
                    React.createElement(
                        Text,
                        {
                            as: 'div',
                            weight: 'bold',
                            size: 'medium',
                            color: config.textColor as any,
                            themeOverride: {
                                warningColor: '#EE6D15',
                            },
                        },
                        title
                    ),
                    React.createElement(
                        View,
                        { as: 'div' },
                        typeof children === 'string'
                            ? React.createElement(Text, { as: 'div', size: 'small' }, children)
                            : children
                    )
                )
            )
        )
    );
};

export default Callout;
